import { useEffect } from "react";
import { create } from "zustand";

/**
 * Who renders the dock rail.
 *
 * The rail is app chrome — it is present in EVERY module (owner rule
 * 2026-08-12), so Shell owns it by default and renders it at the top of the
 * main column. But a page that carries its own full-height left tree (chat) has
 * to put the rail INSIDE its chat column: a Shell-level rail spans the whole
 * main area, which pushes that tree down and leaves a band of header sitting
 * above it (owner, 2026-09-12: "the chat tree container shall go all the way to
 * the top, the header stops there").
 *
 * So a page can CLAIM the rail for the life of its mount. Shell then renders
 * nothing and the page renders `<DockRail />` where it belongs. Exactly one
 * rail exists either way — the claim is what prevents two.
 */
interface DockRailOwnership {
	/** True while a page is rendering the rail itself. */
	claimed: boolean;
	claim: () => void;
	release: () => void;
}

export const useDockRailOwnership = create<DockRailOwnership>((set) => ({
	claimed: false,
	claim: () => set({ claimed: true }),
	release: () => set({ claimed: false }),
}));

/**
 * Claim the dock rail for this page's own header row while it is mounted.
 *
 * Releases on unmount, including on an unmount that races a navigation — the
 * cleanup runs before the next page's effect, so Shell never ends up believing
 * a page that has gone away is still drawing the rail.
 */
export function useOwnDockRail(): void {
	useEffect(() => {
		const { claim, release } = useDockRailOwnership.getState();
		claim();
		return release;
	}, []);
}
